'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import StarRating from '@/components/ui/StarRating';
import { Bed, Bath, Users, Wifi, Car, Waves, Wind, ChevronRight, SlidersHorizontal, CalendarCheck, XCircle } from 'lucide-react';

const apartments = [
  {
    id: 'apt-001',
    name: 'Tranquility Suite',
    tagline: 'Placeholder',
    image: '/assets/images/WhatsApp_Image_2026-04-23_at_2.43.33_PM_1_-1778784107409.jpeg',
    alt: 'Bright modern apartment living room with floor-to-ceiling windows overlooking the Mediterranean sea',
    beds: 2,
    baths: 1,
    maxGuests: 3,
    sqm: 75,
    pricePerNight: 145,
    rating: 4.9,
    reviewCount: 48,
    available: true,
    amenities: ['wifi', 'parking', 'beach', 'ac'],
    badge: 'Most Popular',
    badgeColor: 'bg-primary text-white',
    // Booked date ranges (inclusive)
    bookedRanges: [] as { from: string; to: string }[],
  },
  {
    id: 'apt-002',
    name: 'Restful Space',
    tagline: 'Placeholder',
    image: '/assets/images/WhatsApp_Image_2026-04-23_at_2.43.41_PM-1778784601833.jpeg',
    alt: 'Airy loft apartment with exposed beams, white walls, and large windows with ocean view',
    beds: 2,
    baths: 1,
    maxGuests: 3,
    sqm: 55,
    pricePerNight: 98,
    rating: 4.8,
    reviewCount: 36,
    available: true,
    amenities: ['wifi', 'beach', 'ac'],
    badge: 'Best Value',
    badgeColor: 'bg-secondary text-white',
    bookedRanges: [] as { from: string; to: string }[],
  },
  {
    id: 'apt-003',
    name: 'MIndfulness Escape',
    tagline: 'Placeholder',
    image: '/assets/images/WhatsApp_Image_2026-04-23_at_2.43.33_PM-1778784714516.jpeg',
    alt: 'Spacious villa with private garden, outdoor dining table, and lush Mediterranean plants',
    beds: 2,
    baths: 1,
    maxGuests: 3,
    sqm: 110,
    pricePerNight: 210,
    rating: 4.9,
    reviewCount: 29,
    available: true,
    amenities: ['wifi', 'parking', 'beach', 'ac'],
    badge: 'Family Pick',
    badgeColor: 'bg-accent text-foreground',
    bookedRanges: [] as { from: string; to: string }[],
  },
  {
    id: 'apt-004',
    name: 'Serenity Haven',
    tagline: 'Placeholder',
    image: '/assets/images/WhatsApp_Image_2026-04-23_at_2.43.42_PM_3_-1778785051911.jpeg',
    alt: 'Modern rooftop apartment with panoramic terrace, sun loungers, and stunning coastline views',
    beds: 2,
    baths: 1,
    maxGuests: 3,
    sqm: 90,
    pricePerNight: 185,
    rating: 5.0,
    reviewCount: 21,
    available: false,
    amenities: ['wifi', 'parking', 'beach', 'ac'],
    badge: 'Premium',
    badgeColor: 'bg-foreground text-white',
    bookedRanges: [] as { from: string; to: string }[],
  },
];

const amenityIcons: Record<string, { icon: React.ComponentType<{ size?: number; className?: string }>; label: string }> = {
  wifi: { icon: Wifi, label: 'WiFi' },
  parking: { icon: Car, label: 'Parking' },
  beach: { icon: Waves, label: 'Beach Access' },
  ac: { icon: Wind, label: 'A/C' },
};

type SortOption = 'recommended' | 'price-asc' | 'price-desc' | 'rating';

interface ApartmentGridProps {
  checkIn?: string;
  checkOut?: string;
  guests?: string;
}

function isAvailableForDates(
  apt: (typeof apartments)[0],
  checkIn: string,
  checkOut: string
): boolean {
  // If apartment is marked as permanently unavailable
  if (!apt.available) return false;

  // If no dates selected, show all available apartments
  if (!checkIn || !checkOut) return apt.available;

  const start = new Date(checkIn);
  const end = new Date(checkOut);

  // Check against booked ranges
  for (const range of apt.bookedRanges) {
    const bookedFrom = new Date(range.from);
    const bookedTo = new Date(range.to);
    // Overlap check: requested range overlaps with booked range
    if (start <= bookedTo && end >= bookedFrom) {
      return false;
    }
  }

  return true;
}

function calcNights(checkIn: string, checkOut: string): number {
  if (!checkIn || !checkOut) return 0;
  const diff = new Date(checkOut).getTime() - new Date(checkIn).getTime();
  return Math.max(0, Math.round(diff / (1000 * 60 * 60 * 24)));
}

export default function ApartmentGrid({ checkIn = '', checkOut = '', guests = '2' }: ApartmentGridProps) {
  const [sort, setSort] = useState<SortOption>('recommended');
  const [guestFilter, setGuestFilter] = useState<number>(0);

  const guestNum = parseInt(guests, 10) || 0;
  const nights = calcNights(checkIn, checkOut);
  const datesSelected = !!(checkIn && checkOut);

  const filtered = apartments
    .filter((apt) => {
      // Guest filter from dropdown
      if (guestFilter > 0 && apt.maxGuests < guestFilter) return false;
      // Guest filter from hero search
      if (guestNum > 0 && apt.maxGuests < guestNum) return false;
      return true;
    })
    .map((apt) => ({
      ...apt,
      isAvailable: isAvailableForDates(apt, checkIn, checkOut),
    }))
    .sort((a, b) => {
      // Sort unavailable to bottom when dates are selected
      if (datesSelected && a.isAvailable !== b.isAvailable) {
        return a.isAvailable ? -1 : 1;
      }
      if (sort === 'price-asc') return a.pricePerNight - b.pricePerNight;
      if (sort === 'price-desc') return b.pricePerNight - a.pricePerNight;
      if (sort === 'rating') return b.rating - a.rating;
      return 0;
    });

  const availableCount = filtered.filter((a) => a.isAvailable).length;

  return (
    <section id="apartments-section" className="max-w-screen-2xl mx-auto px-6 lg:px-10 py-16">
      {/* Section header */}
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
        <div>
          <p className="text-xs font-700 uppercase tracking-widest text-primary mb-2">Our Properties</p>
          <h2 className="text-hero-md font-800 text-foreground">Four Perfect Escapes</h2>
          {datesSelected ? (
            <p className="text-muted-foreground mt-1.5 text-sm">
              <span className="font-600 text-foreground">{availableCount}</span> apartment{availableCount !== 1 ? 's' : ''} available
              {' '}for <span className="font-600 text-foreground">{nights} night{nights !== 1 ? 's' : ''}</span>
              {' '}({checkIn} → {checkOut})
            </p>
          ) : (
            <p className="text-muted-foreground mt-1.5 text-sm">
              All apartments are directly managed — no middlemen, no surprises.
            </p>
          )}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 bg-muted rounded-xl px-3 py-2">
            <SlidersHorizontal size={14} className="text-muted-foreground" />
            <select
              value={guestFilter}
              onChange={(e) => setGuestFilter(Number(e.target.value))}
              className="bg-transparent text-sm font-500 text-foreground focus:outline-none">
              <option value={0}>All guests</option>
              <option value={2}>2+ guests</option>
              <option value={4}>4+ guests</option>
              <option value={6}>6+ guests</option>
            </select>
          </div>
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value as SortOption)}
            className="bg-muted text-sm font-500 text-foreground rounded-xl px-3 py-2 focus:outline-none border-none">
            <option value="recommended">Recommended</option>
            <option value="price-asc">Price: Low to High</option>
            <option value="price-desc">Price: High to Low</option>
            <option value="rating">Top Rated</option>
          </select>
        </div>
      </div>

      {/* Date search summary banner */}
      {datesSelected && (
        <div className="flex items-center gap-3 bg-primary/8 border border-primary/20 rounded-xl px-4 py-3 mb-6 text-sm">
          <CalendarCheck size={16} className="text-primary shrink-0" />
          <span className="text-foreground">
            Showing availability for <span className="font-700">{checkIn}</span> → <span className="font-700">{checkOut}</span>
            {nights > 0 && <span className="text-muted-foreground"> · {nights} night{nights !== 1 ? 's' : ''}</span>}
            {guestNum > 0 && <span className="text-muted-foreground"> · {guestNum} guest{guestNum !== 1 ? 's' : ''}</span>}
          </span>
        </div>
      )}

      {/* Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-6">
        {filtered.map((apt) => (
          <div key={apt.id} className={`relative ${!apt.isAvailable && datesSelected ? 'opacity-60' : ''}`}>
            {!apt.isAvailable && datesSelected && (
              <div className="absolute inset-0 z-10 flex items-center justify-center pointer-events-none">
                <div className="bg-white/90 backdrop-blur-sm rounded-xl px-4 py-2 flex items-center gap-2 shadow-md">
                  <XCircle size={16} className="text-red-500" />
                  <span className="text-sm font-700 text-red-600">Not Available</span>
                </div>
              </div>
            )}
            <Link href="/apartment-detail" className={`group block h-full ${!apt.isAvailable && datesSelected ? 'pointer-events-none' : ''}`}>
              <div className="card-base card-hover overflow-hidden h-full flex flex-col">
                {/* Image */}
                <div className="relative aspect-[4/3] overflow-hidden">
                  <AppImage
                    src={apt.image}
                    alt={apt.alt}
                    fill
                    className="object-cover group-hover:scale-105 transition-transform duration-500"
                    sizes="(max-width: 640px) 100vw, (max-width: 1280px) 50vw, 25vw"
                  />
                  {/* Badge */}
                  <span className={`absolute top-3 left-3 text-xs font-700 px-2.5 py-1 rounded-full ${apt.badgeColor}`}>
                    {apt.badge}
                  </span>
                  {/* Availability chip when dates selected */}
                  {datesSelected && apt.isAvailable && (
                    <span className="absolute top-3 right-3 text-xs font-700 px-2.5 py-1 rounded-full bg-green-500 text-white flex items-center gap-1">
                      <CalendarCheck size={11} /> Available
                    </span>
                  )}
                  {/* Price chip */}
                  <div className="absolute bottom-3 right-3 bg-white/95 backdrop-blur-sm rounded-xl px-2.5 py-1.5 shadow-sm">
                    <span className="text-sm font-800 text-foreground tabular-nums">€{apt.pricePerNight}</span>
                    <span className="text-xs text-muted-foreground">/night</span>
                    {datesSelected && nights > 0 && apt.isAvailable && (
                      <div className="text-xs text-primary font-700 text-right">€{apt.pricePerNight * nights} total</div>
                    )}
                  </div>
                </div>

                {/* Content */}
                <div className="p-4 flex flex-col flex-1">
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <h3 className="font-700 text-base text-foreground leading-tight">{apt.name}</h3>
                    <div className="flex items-center gap-1 shrink-0">
                      <span className="text-sm font-700 text-foreground tabular-nums">{apt.rating}</span>
                      <StarRating rating={apt.rating} size={12} />
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground mb-3 leading-relaxed">{apt.tagline}</p>

                  {/* Stats row */}
                  <div className="flex items-center gap-3 text-xs text-muted-foreground mb-3">
                    <span className="flex items-center gap-1"><Bed size={12} />{apt.beds} bed{apt.beds > 1 ? 's' : ''}</span>
                    <span className="flex items-center gap-1"><Bath size={12} />{apt.baths} bath</span>
                    <span className="flex items-center gap-1"><Users size={12} />Up to {apt.maxGuests}</span>
                    <span className="text-muted-foreground/50">·</span>
                    <span>{apt.sqm}m²</span>
                  </div>

                  {/* Amenity chips */}
                  <div className="flex flex-wrap gap-1.5 mb-4">
                    {apt.amenities.map((a) => {
                      const amenity = amenityIcons[a];
                      if (!amenity) return null;
                      const IconComp = amenity.icon;
                      return (
                        <span key={`${apt.id}-amenity-${a}`} className="flex items-center gap-1 text-[11px] font-500 text-muted-foreground bg-muted px-2 py-1 rounded-lg">
                          <IconComp size={11} />
                          {amenity.label}
                        </span>
                      );
                    })}
                  </div>

                  <div className="mt-auto flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">{apt.reviewCount} reviews</span>
                    {apt.isAvailable || !datesSelected ? (
                      <span className="flex items-center gap-1 text-xs font-600 text-primary group-hover:gap-2 transition-all">
                        View details <ChevronRight size={13} />
                      </span>
                    ) : (
                      <span className="text-xs font-600 text-red-500">Unavailable</span>
                    )}
                  </div>
                </div>
              </div>
            </Link>
          </div>
        ))}
      </div>

      {/* No results */}
      {filtered.length === 0 && (
        <div className="text-center py-16">
          <div className="text-5xl mb-4">🏖️</div>
          <h3 className="text-lg font-700 text-foreground mb-2">No apartments match your filters</h3>
          <p className="text-muted-foreground text-sm mb-6">Try adjusting the guest count or removing filters.</p>
          <button onClick={() => setGuestFilter(0)} className="btn-primary">
            Clear Filters
          </button>
        </div>
      )}
    </section>
  );
}